import { InboxOutlined } from "@ant-design/icons";
import { Button, Input, Modal, Progress, Select, Upload } from "antd";
import { useState } from "react";
import { DocumentType } from "../../enum/common";

import { notify } from "../../utils/toast";
import { useUploadDocumentProduction } from "../../query/ProductionQuery";
const { Dragger } = Upload;
const { TextArea } = Input;

interface ModalUploadDocumentProductionProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
}

const ModalUploadDocumentProduction = ({
  projectId,
  isOpen,
  onClose,
}: ModalUploadDocumentProductionProps) => {
  const [category, setCategory] = useState<DocumentType>(DocumentType.DESIGN);
  const [version, setVersion] = useState<string>("v1");
  const [tags, setTags] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [fileList, setFileList] = useState<any[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const uploadMutation = useUploadDocumentProduction();

  const handleUpload = async () => {
    if (!fileList || fileList.length === 0) {
      notify.error("Vui lòng chọn file để upload");
      return;
    }
    const file = fileList[0]?.originFileObj;
    if (!file) {
      notify.error("File không hợp lệ");
      return;
    }
    try {
      setUploadProgress(0);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("documentType", category);
      formData.append("prdProjectId", projectId.toString());
      formData.append("version", version);
      formData.append("note", notes);
      if (tags) {
        formData.append("tag", tags);
      }
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 3000);
      await uploadMutation.mutateAsync(formData as any);
      clearInterval(progressInterval);
      setUploadProgress(100);
      setTimeout(() => {
        notify.success("Upload tài liệu thành công");
        onClose();
        setCategory(DocumentType.DESIGN);
        setVersion("v1");
        setTags("");
        setNotes("");
        setFileList([]);
        setUploadProgress(0);
      }, 500);
    } catch (error) {
      setUploadProgress(0);
      notify.error("Upload tài liệu thất bại");
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={850}
      closeIcon={
        <span className="text-gray-400 hover:text-white text-xl">×</span>
      }
      styles={{
        content: {
          padding: 0,
        },
      }}
    >
      <div className="p-6">
        <h2 className="text-xl font-semibold mb-6">Upload tài liệu mới</h2>
        <Dragger
          multiple={false}
          fileList={fileList}
          onChange={({ fileList: newFileList }) => setFileList(newFileList)}
          beforeUpload={(file) => {
            const lowerName = file.name.toLowerCase();
            const isZipOrRar =
              lowerName.endsWith(".zip") || lowerName.endsWith(".rar");
            if (!isZipOrRar) {
              notify.error("Chỉ chấp nhận file .zip hoặc .rar !");
              return Upload.LIST_IGNORE;
            }
            return false;
          }}
          maxCount={1}
          accept=".zip,.rar"
          className="mb-6"
          style={{
            background: "transparent",
            border: "2px dashed #4a5568",
            borderRadius: "8px",
          }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined style={{ color: "#6b7280", fontSize: "48px" }} />
          </p>
          <p className="text-gray-300 text-base mb-2">
            Kéo thả file vào đây hoặc click để chọn
          </p>
          <p className="text-gray-500 text-sm">
            Chỉ chấp nhận file .zip, .rar (Max 10MB)
          </p>
        </Dragger>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Danh mục <span className="text-red-500">*</span>
            </label>
            <Select
              value={category}
              onChange={setCategory}
              className="w-full"
              size="large"
              options={Object.entries(DocumentType).map(([key, value]) => ({
                label:
                  key.charAt(0) + key.slice(1).toLowerCase().replace(/_/g, " "),
                value,
              }))}
              style={{
                width: "100%",
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Version</label>
            <Input
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="v1"
              size="large"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Tags</label>
            <Input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="PCB, Electronics, Sensor (phân cách bằng dấu phẩy)"
              size="large"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Ghi chú</label>
            <TextArea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Thêm ghi chú về tài liệu..."
              rows={4}
            />
          </div>
        </div>

        {uploadMutation.isPending && uploadProgress > 0 && (
          <div className="mt-4">
            <Progress
              percent={uploadProgress}
              status={uploadProgress === 100 ? "success" : "active"}
              strokeColor={{
                "0%": "#108ee9",
                "100%": "#87d068",
              }}
            />
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <Button
            onClick={onClose}
            size="large"
            disabled={uploadMutation.isPending}
          >
            Hủy
          </Button>
          <Button
            type="primary"
            onClick={handleUpload}
            size="large"
            loading={uploadMutation.isPending}
            disabled={uploadMutation.isPending}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {uploadMutation.isPending ? "Đang upload..." : "Upload"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ModalUploadDocumentProduction;
